import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Card,
    CardBody,
    Container,
    Heading,
    Icon,
    ListItem,
    Text,
    UnorderedList,
    VStack,
    HStack,
    Divider,
} from "@chakra-ui/react";
import { FiUpload, FiCopy } from "react-icons/fi";
import { MdFileDownload } from "react-icons/md";
import { UploadModal } from "./components/UploadModal";
import React, { useCallback, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { selectUserData } from "store/auth/authSlice";
import { useAppSelector } from "hooks/store";
import { TDealerCRM } from "api/dealershipApi";
import useUrlQuery from "hooks/useUrlQuery";
import { CommonUrlParams } from "utils/urlParams";
import { useNavigate } from "react-router-dom";
import ERoutes from "types/Routes";

type ImportFileUrlParams = CommonUrlParams & {
    crm?: TDealerCRM;
}

export function ImportFilePage() {

    const navigate = useNavigate();
    
    const auth = useAppSelector(selectUserData);
    
    const { setMultipleParams } = useUrlQuery<ImportFileUrlParams>();

    const userAuth = useAppSelector(selectUserData);

    const [isUploadModalOpen, setIsUploadModalOpen] = React.useState(false);
    
    useEffect(() => {
        
        setMultipleParams({
            inventory: userAuth.inventory.value,
            dealership_uid: userAuth.dealerV2?.uid,
        });
            
        if (!userAuth.dealerV2?.crm || userAuth.dealerV2.crm === null) {
            navigate(ERoutes.Home);
        }
        
    }, [userAuth.inventory.value, userAuth.dealerV2?.uid, userAuth.dealerV2?.crm]);

    const getCurrentCRM = useMemo(() => () => {
        return csvFiles.filter(file => file.crm === auth.dealerV2?.crm);
    }, [auth.dealerV2.crm]);
    
    const handleUploadClick = () => {
        setIsUploadModalOpen(true);
    };

    const handleCopyLink = () => {
        // get current URL and copy to clipboard
        const currentUrl = window.location.href;
        navigator.clipboard.writeText(currentUrl)
            .then(() => {
                toast.success("Upload link copied to clipboard!");
            })
            .catch((err) => {
                toast.error("Failed to copy link. Please try again.");
            });
    };
    
    const downloadCSVFromUrl = useCallback((file: TemplateFile) => () => {
        const a = document.createElement("a");
        a.href = file.url
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
    }, [csvFiles, auth.dealerV2]);
    
    return (
        <>
            <UploadModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
            />
            <Container maxW="4xl" py={10}>
                <VStack spacing={8} align="stretch">
                    {/* Page Header */}
                    <Box>
                        <Heading size="lg">Import Sold File</Heading>
                        <Text color="gray.500" mt={2}>
                            Upload your dealership sales records in CSV format.
                        </Text>
                        <Box
                            display="flex"
                            flexDirection="row"
                            gap={4}
                        >
                            {getCurrentCRM().map((file, index) => (
                                <Button
                                    key={index}
                                    leftIcon={<Icon as={MdFileDownload} />}
                                    variant="outline"
                                    size="lg"
                                    borderRadius="xl"
                                    onClick={downloadCSVFromUrl(file)}
                                    marginTop="20px"
                                >
                                    {getCurrentCRM().length > 1
                                        ? `Download Template ${index + 1}`
                                        : 'Download Template'}
                                </Button>
                            ))}
                        </Box>
                    </Box>

                    {/* Instruction Card */}
                    <Card borderRadius="xl" boxShadow="lg" bg="white">
                        <CardBody>
                            <VStack align="start" spacing={5}>
                                <Alert status="info" borderRadius="md">
                                    <AlertIcon />
                                    Please follow the upload instructions carefully.
                                </Alert>
                                
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    gap={4}
                                    width="100%"
                                >
                                    {getCurrentCRM().map((file, index) => (
                                        <Box 
                                            key={index}
                                            flex="1"
                                        >
                                            <Heading size="sm" mb={3}>
                                                {getCurrentCRM().length > 1
                                                    ? `Template ${index + 1} required columns:`
                                                    : 'Required columns:'}
                                            </Heading>

                                            <UnorderedList spacing={2} pl={5} color="gray.700">
                                                {file.columns.map((column) => (
                                                    <ListItem key={column}>{column}</ListItem>
                                                ))}
                                            </UnorderedList>
                                        </Box>
                                    ))}
                                </Box>

                                <Divider />

                                <Text fontSize="sm" color="gray.500">
                                    Only <strong>.csv</strong> files are supported. Please ensure
                                    your file is properly formatted before uploading.
                                </Text>
                            </VStack>
                        </CardBody>
                    </Card>

                    {/* Action Section */}
                    <HStack spacing={4}>
                        <Button
                            leftIcon={<Icon as={FiUpload} />}
                            colorScheme="blue"
                            size="lg"
                            borderRadius="xl"
                            boxShadow="md"
                            onClick={handleUploadClick}
                        >
                            Upload CSV File
                        </Button>

                        <Button
                            leftIcon={<Icon as={FiCopy} />}
                            variant="outline"
                            size="lg"
                            borderRadius="xl"
                            onClick={handleCopyLink}
                        >
                            Copy Upload Link
                        </Button> 
                    </HStack>
                </VStack>
            </Container>
        </>
    );
}

type TemplateFile = {
    name: string;
    url: string;
    crm: TDealerCRM;
    columns: string[];
}

const csvFiles: TemplateFile[] = [
    {
        name: "vinsolutions_sold_template.csv",
        url: "/csv/vinsolutions_sold_template.csv",
        crm: "vinsolutions",
        columns: [
            "SoldStatus",
            "SoldDate",
            "LeadCreatedDate",
            "FirstName",
            "MiddleName",
            "LastName",
            "CellPhone",
            "LeadSource",
            "SellingPrice",
            "VehicleYear",
            "VehicleMake",
            "VehicleModel",
            "VehicleStockNumber",
            "VehicleVIN",
            "InventoryType"
        ]
    },
    {
        name: "eleads_sold_template.csv",
        url: "/csv/eleads_sold_template.csv",
        crm: "eleads",
        columns: [
            "Row",
            "Name",
            "Cell Phone",
            "Stock#",
            "VIN",
            "Vehicle",
            "Date Active",
            "Date Sold",
            "Sale Price",
            "N/U",
            "Up Type",
            "Source",
            "Deal Status"
        ]
    },
    {
        name: "dealersocket_sold_template.csv",
        url: "/csv/dealersocket_sold_template.csv",
        crm: "dealersocket",
        columns: [
            "First Name",
            "Last Name",
            "VIN",
            "Stock Num",
            "Year",
            "Make",
            "Model",
            "Purchase Date",
            "Gross Profit"
        ]
    },
    {
        name: "drivecentric_sold_template.csv",
        url: "/csv/drivecentric_sold_template.csv",
        crm: "drivecentric",
        columns: [
            'Buyer',
            'Date Delivered',
            'Vehicle',
            'New/Used',
            'VIN',
            'Phone',
            'Stock No.'
        ]
    },
    {
        name: "tekion_sold_template_v1.csv",
        url: "/csv/tekion_sold_template_v1.csv",
        crm: "tekion",
        columns: [
            "Stock Type",
            "Status",
            "Customer",
            "Customer Number",
            "Stock#",
            "VIN #",
            "Vehicle",
            "Sold Date",
            "Lead Type",
            "Lead Source"
        ]
    },
    {
        name: "tekion_sold_template_v2.csv",
        url: "/csv/tekion_sold_template_v2.csv",
        crm: "tekion",
        columns: [
            "VIN #",
            "Stock #",
            "Buyer",
            "Source Type (Detailed)",
            "Source Name",
            "Stock Type",
            "Selling Price",
            "Lead Creation Date",
            "Deal Status",
            "Mobile phone",
            "Sold Date"
        ]
    }
];